import React, { useState, useEffect } from 'react';
import './GetQuote.css';

import { Heading } from '../../../components';

import { FaUser, FaEnvelope, FaPhone, FaCogs, FaDollarSign, FaCalendar, FaMapMarkerAlt, FaComment, FaAngleDown, FaCamera } from 'react-icons/fa';
import axios from "axios";

const API_PATH = 'https://secura-react.asdesignsgalaxy.com/api/phpmailer/quote.php';

const EMAIL_REGEX = /^\w+([.-]?\w+)*@\w+([.-]?\w+)*(\.\w{2,3})+$/;
const PHONE_REGEX = /^[+]?[(]?[0-9]{1,4}[)]?[-\s./0-9]*$/;



const GetQuote = () => {

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    number: '',
    service: '',
    numDevices: '',
    pricing: '',
    installDate: '',
    address: '',
    description: ''
  });

  const [mailSent, setMailSent] = useState(false);
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (mailSent) {
      const timer = setTimeout(() => setMailSent(false), 3000);
      return () => clearTimeout(timer);
    }
  }, [mailSent]);

  const handleSubmitForm = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError('');

    try {
      const validationErrors = [];
      
      if (!formData.name.trim()) validationErrors.push('Name is required');
      if (!formData.email.trim()) {
        validationErrors.push('Email is required');
      } else if (!EMAIL_REGEX.test(formData.email)) {
        validationErrors.push('Invalid email format');
      }
      if (!formData.number.trim()) {
        validationErrors.push('Phone number is required');
      } else if (!PHONE_REGEX.test(formData.number)) {
        validationErrors.push('Invalid phone number format');
      }
      if (!formData.service) validationErrors.push('Service is required');
      if (!formData.numDevices) validationErrors.push('Number of devices is required');
      if (!formData.pricing) validationErrors.push('Pricing plan is required');
      if (!formData.installDate) validationErrors.push('Installation date is required');
      if (!formData.address.trim()) validationErrors.push('Address is required');
      if (!formData.description.trim()) validationErrors.push('Project description is required');

      if (validationErrors.length > 0) {
        throw new Error(validationErrors.join(' | '));
      }

      const postData = new FormData();
      Object.entries(formData).forEach(([key, value]) => {
        postData.append(key, value);
      });

      const response = await axios.post(API_PATH, postData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      if (!response.data.sent) {
        throw new Error(response.data.message || 'Form submission failed');
      }

      setMailSent(true);
      setFormData({
        name: '',
        email: '',
        number: '',
        service: '',
        numDevices: '',
        pricing: '',
        installDate: '',
        address: '',
        description: ''
      });
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'An error occurred');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };
 
  return (
    <section className="custom-form get-quote linear-bg">
      <div className="box-container">
        <div className="content">
          <form className="dark-form" onSubmit={handleSubmitForm}>

            {/* Form Heading */}
            <Heading title={"Request a Quote"} subTitle={"Get a Quote"} sideHeading linear />

            <div className="input-box">

              {/* Full Name Input Field */}
              <div className="input-field">
                <label htmlFor="name"><FaUser /></label>
                <input
                  type="text"
                  name="name"
                  className="box"
                  id="name"
                  placeholder="Full Name"
                  value={formData.name}
                  onChange={handleInputChange}
                  required
                  disabled={isSubmitting}
                />
              </div>

              {/* Email Input Field */}
              <div className="input-field">
                <label htmlFor="email"><FaEnvelope /></label>
                <input
                  type="email"
                  name="email"
                  className="box"
                  id="email"
                  placeholder="Email Address"
                  value={formData.email}
                  onChange={handleInputChange}
                  required
                  disabled={isSubmitting}
                />
              </div>
            </div>

            <div className="input-box">

              {/* Phone Number Input Field */}
              <div className="input-field">
                <label htmlFor="number"><FaPhone /></label>
                <input
                  type="number"
                  name="number"
                  className="box"
                  id="number"
                  placeholder="Phone Number"
                  value={formData.number}
                  onChange={handleInputChange}
                  required
                  disabled={isSubmitting}
                />
              </div>

              {/* Service Required Field */}
              <div className="select">
                <div className="select-container">
                  <label htmlFor="service"><FaCogs /></label>
                  <select
                    className="select-box"
                    id="service"
                    name="service"
                    value={formData.service}
                    onChange={handleInputChange}
                    required
                    disabled={isSubmitting}
                  >
                    <option value="">Service Required</option>
                    <option value="Installation">Installation</option>
                    <option value="Maintenance">Maintenance</option>
                    <option value="Monitoring">Monitoring</option>
                  </select>
                  <div className="icon-container"><FaAngleDown className="icon" /></div>
                </div>
              </div>

            </div>

            <div className="input-box">

              {/* Number of Devices Field */}
              <div className="input-field">
                <label htmlFor="numDevices"><FaCamera /></label>
                <input
                  type="number"
                  name="numDevices"
                  className="box"
                  id="numDevices"
                  placeholder="Number of Devices"
                  value={formData.numDevices}
                  onChange={handleInputChange}
                  required
                  disabled={isSubmitting}
                />
              </div>

              {/* Pricing Field */}
              <div className="select">
                <div className="select-container">
                  <label htmlFor="pricing"><FaDollarSign /></label>
                  <select
                    className="select-box"
                    id="pricing"
                    name="pricing"
                    value={formData.pricing}
                    onChange={handleInputChange}
                    required
                    disabled={isSubmitting}
                  >
                    <option value="">Choose Plan</option>
                    <option value="Basic Plan">Basic Plan</option>
                    <option value="Standard Plan">Standard Plan</option>
                    <option value="Premium Plan">Premium Plan</option>
                  </select>
                  <div className="icon-container"><FaAngleDown className="icon" /></div>
                </div>
              </div>

            </div>

            <div className="input-box">

              {/* Installation Date Field */}
              <div className="input-field">
                <label htmlFor="installDate"><FaCalendar /></label>
                <input
                  type="date"
                  name="installDate"
                  className="box"
                  id="installDate"
                  value={formData.installDate}
                  onChange={handleInputChange}
                  required
                  disabled={isSubmitting}
                />
              </div>

              {/* Address Field */}
              <div className="input-field">
                <label htmlFor="address"><FaMapMarkerAlt /></label>
                <input
                  type="text"
                  name="address"
                  className="box"
                  id="address"
                  placeholder="Address"
                  value={formData.address}
                  onChange={handleInputChange}
                  required
                  disabled={isSubmitting}
                />
              </div>

            </div>

            {/* Project Description Text Area */}
            <div className="input-field text-area">
              <label htmlFor="description"><FaComment /></label>
              <textarea
                cols="30"
                rows="10"
                name="description"
                className="box"
                id="description"
                placeholder="Project Description"
                value={formData.description}
                onChange={handleInputChange}
                required
                disabled={isSubmitting}
              ></textarea>
            </div>

            {/* Submit Button */}
            <button 
              type="submit" 
              className="btn" 
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Submitting...' : 'Get Quote'}
            </button>

            {/* Error and Success Messages */}
            {error && (
              <div className="alert error">
                {error.split(' | ').map((msg, i) => (
                  <span key={i}>{msg}</span>
                ))}
              </div>
            )}
            {mailSent && <div className="alert success"><span>Request Submitted Successfully</span></div>}
          </form>
        </div>
      </div>
    </section>
  );
}

export default GetQuote;